import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider, useLang } from "./i18n/LangContext";

const LangKeyed = ({ children }) => { const { lang } = useLang(); return <React.Fragment key={lang}>{children}</React.Fragment>; };
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardShell from "./pages/DashboardShell";
import AdminConsole from "./pages/AdminConsole";
import PnL from "./pages/PnL";
import Journey from "./pages/Journey";
import Activity from "./pages/Activity";
import Landing from "./pages/Landing";
import PendingApproval from "./pages/PendingApproval";
import SharePage from "./pages/SharePage";
import SubmitPage from "./pages/SubmitPage";
import { Toaster } from "./components/ui/sonner";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
    </div>
  );
}

// Staff (admin/super) console. If they're impersonating a student, send them into the CRM.
function AdminRoute() {
  const { user, loading, isStaff, acting } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/dashboard" replace />;
  if (acting) return <Navigate to="/dashboard" replace />;
  return <AdminConsole />;
}

// The CRM. Students use it directly; staff use it only while impersonating a student.
function DashboardRoute() {
  const { user, loading, isStaff, acting } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (isStaff && !acting) return <Navigate to="/admin" replace />;
  if (!isStaff && user.status !== "active") return <PendingApproval />;
  return <DashboardShell />;
}

function PnLRoute() {
  const { user, loading, isSuper } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // P&L is the founders' books — success managers and students never see it.
  if (!isSuper) return <Navigate to="/admin" replace />;
  return <PnL />;
}

function JourneyRoute() {
  const { user, loading, isStaff, acting } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/dashboard" replace />;
  if (acting) return <Navigate to="/dashboard" replace />;
  return <Journey />;
}

function ActivityRoute() {
  const { user, loading, isStaff, acting } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/dashboard" replace />;
  if (acting) return <Navigate to="/dashboard" replace />;
  return <Activity />;
}

function PublicOnly({ children }) {
  const { user, loading, isStaff } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={isStaff ? "/admin" : "/dashboard"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Process Google OAuth callback FIRST (synchronously) to avoid race conditions.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><AuthPage /></PublicOnly>} />
      <Route path="/admin" element={<AdminRoute />} />
      <Route path="/pnl" element={<PnLRoute />} />
      <Route path="/journey" element={<JourneyRoute />} />
      <Route path="/activity" element={<ActivityRoute />} />
      <Route path="/dashboard/*" element={<DashboardRoute />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route path="/submit/:token" element={<SubmitPage />} />
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LangProvider>
          <BrowserRouter>
            <LangKeyed><AppRouter /></LangKeyed>
          </BrowserRouter>
        </LangProvider>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
